import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Save, Plus, Trash2, Eye, EyeOff, AlertTriangle, Info, CheckCircle, XCircle, Wrench, Pencil, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReminderSettings = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Contact settings
  const [contactSettings, setContactSettings] = useState({
    whatsapp_number: '',
    billing_email: '',
    admin_name: ''
  });
  
  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    message: '',
    type: 'info', // info, warning, maintenance, success
    is_active: true,
    show_dismiss: true
  });

  // Redirect if not superadmin
  useEffect(() => {
    if (user && !['super_admin', 'superadmin'].includes(user.role?.toLowerCase())) {
      toast.error('Akses ditolak');
      navigate('/app/dashboard');
    }
  }, [user, navigate]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch reminder settings
      const settingsRes = await axios.get(`${API_URL}/api/v2/reminder-settings`, { headers });
      if (settingsRes.data.success) {
        setContactSettings(settingsRes.data.contact || {
          whatsapp_number: '',
          billing_email: '',
          admin_name: ''
        });
        setAnnouncements(settingsRes.data.announcements || []);
      }
    } catch (error) {
      console.error('Error fetching reminder settings:', error);
      // Use defaults if endpoint doesn't exist yet
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveContact = async () => {
    setIsSaving(true);
    try {
      const response = await axios.put(`${API_URL}/api/v2/reminder-settings/contact`, contactSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Pengaturan kontak berhasil disimpan');
      }
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan kontak');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.message) {
      toast.error('Judul dan pesan wajib diisi');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/v2/reminder-settings/announcements`, newAnnouncement, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Pengumuman berhasil ditambahkan');
        setNewAnnouncement({
          title: '',
          message: '',
          type: 'info',
          is_active: true,
          show_dismiss: true
        });
        fetchData();
      }
    } catch (error) {
      toast.error('Gagal menambahkan pengumuman');
    }
  };

  const handleToggleAnnouncement = async (id, isActive) => {
    try {
      await axios.put(`${API_URL}/api/v2/reminder-settings/announcements/${id}`, 
        { is_active: !isActive },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(isActive ? 'Pengumuman dinonaktifkan' : 'Pengumuman diaktifkan');
      fetchData();
    } catch (error) {
      toast.error('Gagal mengubah status pengumuman');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Yakin ingin menghapus pengumuman ini?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/v2/reminder-settings/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pengumuman berhasil dihapus');
      fetchData();
    } catch (error) {
      toast.error('Gagal menghapus pengumuman');
    }
  };

  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  const handleEditAnnouncement = (ann) => {
    setEditingAnnouncement({
      id: ann.id,
      title: ann.title,
      message: ann.message,
      type: ann.type,
      is_active: ann.is_active
    });
  };

  const handleSaveEdit = async () => {
    if (!editingAnnouncement) return;
    
    try {
      await axios.put(`${API_URL}/api/v2/reminder-settings/announcements/${editingAnnouncement.id}`, editingAnnouncement, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pengumuman berhasil diupdate');
      setEditingAnnouncement(null);
      fetchData();
    } catch (error) {
      toast.error('Gagal mengupdate pengumuman');
    }
  };

  const getTypeConfig = (type) => {
    switch (type) {
      case 'warning':
        return { icon: AlertTriangle, color: 'text-amber-700', bg: 'bg-amber-100', border: 'border-amber-400' };
      case 'maintenance':
        return { icon: Wrench, color: 'text-violet-700', bg: 'bg-violet-100', border: 'border-violet-400' };
      case 'success':
        return { icon: CheckCircle, color: 'text-emerald-700', bg: 'bg-emerald-100', border: 'border-emerald-400' };
      case 'error':
        return { icon: XCircle, color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-400' };
      default: // info
        return { icon: Info, color: 'text-sky-700', bg: 'bg-sky-100', border: 'border-sky-400' };
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 space-y-6" data-testid="reminder-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 dark:text-slate-200">
            <Bell className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
            Pengaturan Reminder & Pengumuman
          </h1>
          <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">
            Kelola informasi kontak untuk reminder dan pengumuman ke semua tenant
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contact Settings */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b border-slate-100 dark:border-slate-700">
            <CardTitle className="text-lg">Pengaturan Kontak Reminder</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label htmlFor="whatsapp">Nomor WhatsApp Admin</Label>
              <Input
                id="whatsapp"
                placeholder="6281234567890"
                value={contactSettings.whatsapp_number}
                onChange={(e) => setContactSettings({ ...contactSettings, whatsapp_number: e.target.value })}
                className="mt-1"
                data-testid="whatsapp-input"
              />
              <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Format: 62xxxxxxxxxx (tanpa + atau spasi)</p>
            </div>
            
            <div>
              <Label htmlFor="email">Email Billing</Label>
              <Input
                id="email"
                type="email"
                placeholder="billing@example.com"
                value={contactSettings.billing_email}
                onChange={(e) => setContactSettings({ ...contactSettings, billing_email: e.target.value })}
                className="mt-1"
                data-testid="email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="admin_name">Nama Admin</Label>
              <Input
                id="admin_name"
                placeholder="Admin RadiusLink"
                value={contactSettings.admin_name}
                onChange={(e) => setContactSettings({ ...contactSettings, admin_name: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <Button 
              onClick={handleSaveContact} 
              disabled={isSaving}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-contact-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Menyimpan...' : 'Simpan Pengaturan Kontak'}
            </Button>
          </CardContent>
        </Card>

        {/* New Announcement */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b border-slate-100 dark:border-slate-700">
            <CardTitle className="text-lg">Buat Pengumuman Baru</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label htmlFor="ann_title">Judul Pengumuman</Label>
              <Input
                id="ann_title"
                placeholder="Contoh: Maintenance Server"
                value={newAnnouncement.title}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                className="mt-1"
                data-testid="announcement-title-input"
              />
            </div>
            
            <div>
              <Label htmlFor="ann_message">Pesan</Label>
              <Textarea
                id="ann_message"
                placeholder="Contoh: Server akan maintenance pada tanggal..."
                value={newAnnouncement.message}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                className="mt-1"
                rows={3}
                data-testid="announcement-message-input"
              />
            </div>
            
            <div>
              <Label>Tipe Pengumuman</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['info', 'warning', 'maintenance', 'success'].map((type) => {
                  const config = getTypeConfig(type);
                  const Icon = config.icon;
                  return (
                    <button
                      key={type}
                      onClick={() => setNewAnnouncement({ ...newAnnouncement, type })}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border transition-all
                        ${newAnnouncement.type === type 
                          ? `${config.bg} ${config.border} ${config.color}` 
                          : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                    >
                      <Icon className="w-4 h-4" />
                      {type === 'info' ? 'Info' : type === 'warning' ? 'Peringatan' : type === 'maintenance' ? 'Maintenance' : 'Sukses'}
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={newAnnouncement.show_dismiss}
                  onCheckedChange={(checked) => setNewAnnouncement({ ...newAnnouncement, show_dismiss: checked })}
                />
                <Label className="text-sm">Bisa ditutup oleh user</Label>
              </div>
            </div>
            
            <Button 
              onClick={handleAddAnnouncement}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="add-announcement-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Tambah Pengumuman
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Announcements List */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b border-slate-100 dark:border-slate-700">
          <CardTitle className="text-lg">Daftar Pengumuman</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          ) : announcements.length === 0 ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <Bell className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>Belum ada pengumuman</p>
            </div>
          ) : (
            <div className="space-y-4">
              {announcements.map((ann) => {
                const config = getTypeConfig(ann.type);
                const Icon = config.icon;
                return (
                  <div 
                    key={ann.id} 
                    className={`border-2 rounded-lg p-4 ${config.bg} ${config.border} ${!ann.is_active ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <Icon className={`w-5 h-5 mt-0.5 ${config.color}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className={`font-semibold ${config.color}`}>{ann.title}</h3>
                            <Badge variant={ann.is_active ? 'default' : 'secondary'} className="text-xs">
                              {ann.is_active ? 'Aktif' : 'Nonaktif'}
                            </Badge>
                            <Badge variant="outline" className={`text-xs ${config.color} ${config.border}`}>
                              {ann.type === 'info' ? 'Info' : ann.type === 'warning' ? 'Peringatan' : ann.type === 'maintenance' ? 'Maintenance' : ann.type === 'success' ? 'Sukses' : ann.type}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">{ann.message}</p>
                          {ann.created_at && (
                            <p className="text-xs text-slate-400 mt-2 dark:text-slate-500">
                              Dibuat: {new Date(ann.created_at).toLocaleDateString('id-ID', { 
                                day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditAnnouncement(ann)}
                          title="Edit"
                          className="border-blue-300 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleAnnouncement(ann.id, ann.is_active)}
                          title={ann.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                        >
                          {ann.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteAnnouncement(ann.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      {editingAnnouncement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader className="flex flex-row items-center justify-between border-b">
              <CardTitle>Edit Pengumuman</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setEditingAnnouncement(null)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label>Judul</Label>
                <Input
                  value={editingAnnouncement.title}
                  onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, title: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Pesan</Label>
                <Textarea
                  value={editingAnnouncement.message}
                  onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, message: e.target.value })}
                  className="mt-1"
                  rows={3}
                />
              </div>
              <div>
                <Label>Tipe</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['info', 'warning', 'maintenance', 'success'].map((type) => {
                    const config = getTypeConfig(type);
                    const TypeIcon = config.icon;
                    return (
                      <button
                        key={type}
                        onClick={() => setEditingAnnouncement({ ...editingAnnouncement, type })}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border transition-all
                          ${editingAnnouncement.type === type 
                            ? `${config.bg} ${config.border} ${config.color}` 
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                      >
                        <TypeIcon className="w-4 h-4" />
                        {type === 'info' ? 'Info' : type === 'warning' ? 'Peringatan' : type === 'maintenance' ? 'Maintenance' : 'Sukses'}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setEditingAnnouncement(null)}>
                  Batal
                </Button>
                <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={handleSaveEdit}>
                  <Save className="w-4 h-4 mr-2" />
                  Simpan
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Preview */}
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-900/30">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-blue-800 mb-2">Cara Kerja</h3>
          <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside dark:text-blue-300">
            <li><strong>Pengaturan Kontak:</strong> Nomor WhatsApp dan Email yang akan ditampilkan di banner reminder langganan.</li>
            <li><strong>Pengumuman:</strong> Pesan yang akan ditampilkan ke semua tenant yang login (misalnya: info maintenance, update fitur, dll).</li>
            <li><strong>Pengumuman Aktif:</strong> Hanya pengumuman dengan status &quot;Aktif&quot; yang akan ditampilkan.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReminderSettings;
